import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    insurance_type: 'Private Health Insurance',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d630jlhov5qgbon31oi0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          insurance_type: 'Private Health Insurance',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <h3 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get Your Free Health Insurance Quote
          </h3>
          <p className="text-gray-600 text-base max-w-2xl mx-auto leading-relaxed">
            Fill out the form below and our <strong>Kerrville health insurance experts</strong> will contact you within 24 hours 
            with a personalized quote tailored to your healthcare needs.
          </p>
        </div>

        <form
          id="contact_form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-[#F0F8FF] rounded-2xl p-10 shadow-lg"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6] text-sm"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6] text-sm"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6] text-sm"
                placeholder="830-203-6201"
              />
            </div>
            <div>
              <label htmlFor="insurance_type" className="block text-sm font-semibold text-gray-900 mb-2">
                Insurance Type <span className="text-red-500">*</span>
              </label>
              <select
                id="insurance_type"
                name="insurance_type"
                value={formData.insurance_type}
                onChange={(e) => setFormData({ ...formData, insurance_type: e.target.value })}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6] text-sm cursor-pointer"
              >
                <option value="Private Health Insurance">Private Health Insurance</option>
                <option value="Group Health Insurance">Group Health Insurance</option>
                <option value="Medicare">Medicare</option>
                <option value="Medicare Advantage">Medicare Advantage</option>
                <option value="Medicare Supplement">Medicare Supplement</option>
                <option value="Prescription Drug Plans">Prescription Drug Plans</option>
                <option value="Dental Insurance">Dental Insurance</option>
                <option value="Vision Insurance">Vision Insurance</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
              Additional Information
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setFormData({ ...formData, message: e.target.value });
                }
              }}
              rows={5}
              maxLength={500}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6] text-sm resize-none"
              placeholder="Tell us about your health insurance needs..."
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-[#0077B6] text-white text-base font-semibold rounded-lg hover:bg-[#005F8C] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Sending...' : 'Request Free Quote'}
          </button>

          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium">
                Thank you! Your quote request has been received. We'll contact you within 24 hours.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium">
                Sorry, there was an error submitting your request. Please call us at 830-203-6201.
              </p>
            </div>
          )}
        </form>

        {/* FAQ Section */}
        <div className="mt-20">
          <h4 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            Frequently Asked Questions
          </h4>
          <div className="space-y-6">
            <div className="bg-[#F0F8FF] rounded-xl p-6">
              <h5 className="text-lg font-semibold text-gray-900 mb-2">
                What types of health insurance does West Insurance offer in Kerrville?
              </h5>
              <p className="text-gray-600 text-sm leading-relaxed">
                West Insurance offers comprehensive health insurance solutions including private health insurance, 
                group health insurance for businesses, Medicare plans, Medicare Advantage, Medicare Supplement, 
                prescription drug plans, dental insurance, and vision insurance for Kerrville and Kerr County residents.
              </p>
            </div>
            <div className="bg-[#F0F8FF] rounded-xl p-6">
              <h5 className="text-lg font-semibold text-gray-900 mb-2">
                Where is West Insurance located in Kerrville, TX?
              </h5>
              <p className="text-gray-600 text-sm leading-relaxed">
                West Insurance is located at 243 La Reata Rd S, Kerrville, TX 78028. We serve all of Kerr County 
                and the surrounding Texas Hill Country area with expert health insurance guidance.
              </p>
            </div>
            <div className="bg-[#F0F8FF] rounded-xl p-6">
              <h5 className="text-lg font-semibold text-gray-900 mb-2">
                How can I get a free health insurance quote from West Insurance?
              </h5>
              <p className="text-gray-600 text-sm leading-relaxed">
                You can get a free health insurance quote by calling us at 830-203-6201, visiting our office at 
                243 La Reata Rd S in Kerrville, or filling out our online contact form. Our experienced agents 
                will help you find the right health coverage for your needs and budget.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
